/**
 * Discriminator values for the consolidated managed-catalog table
 * (`catalog_value`). Each value identifies one logical lookup catalog. New
 * catalogs are added here + in CATALOG_TYPE_REGISTRY — no new table or
 * migration required (PD-14226, Option A). Stored as a varchar so the set
 * can grow without a schema change.
 */
export enum CatalogType {
	MerchandiserManufacturer = 'merchandiser_manufacturer',
	MerchandiserModel = 'merchandiser_model',
	/**
	 * Tenant-managed list of client business types (PD-14326). Unlike the
	 * merchandiser catalogs, this one is a SUGGESTION source only —
	 * `client.businessType` stays free-text with no FK, so the integration and
	 * manual entry keep writing raw strings. The catalog values simply seed the
	 * client-form Business Type typeahead alongside values already on clients.
	 */
	ClientBusinessType = 'client_business_type',
	/** Vehicle make (flat parent of vehicle model). PD-14338. */
	VehicleMake = 'vehicle_make',
	/** Vehicle model — child of a make, like merchandiser model. PD-14338. */
	VehicleModel = 'vehicle_model',
	/**
	 * Tenant-managed softphone call dispositions (PD-14436) — the outcomes a
	 * rep can pick in the post-call wrap-up panel. The chosen value's NAME is
	 * stored on `call.dispositionCode` (free varchar), so legacy free-text
	 * codes keep rendering and renames only affect future calls. Seeded with
	 * seven generic defaults per tenant.
	 */
	SoftphoneCallDisposition = 'softphone_call_disposition',
	/**
	 * Dispatch label lists (PD-16048), moved here from the
	 * `distribution-config` preference so every managed value list is edited
	 * on one page. The value NAME is what the driver flows show and, for the
	 * adjustment and stop-outcome reasons, what gets stored verbatim on the
	 * consuming row (`inventory_movement.reason`, `trip_stop.terminationReason`).
	 * A pause stop stores `catalogValueCode(name)` on `trip_stop.pauseCategory`
	 * (varchar 40), which for the seeded defaults is the same code the
	 * preference used to hold (`meal`, `fuel`, ...).
	 */
	DispatchAdjustmentReason = 'dispatch_adjustment_reason',
	DispatchStopOutcomeReason = 'dispatch_stop_outcome_reason',
	DispatchPauseCategory = 'dispatch_pause_category',
	/**
	 * PD-16801 — how a client visit was made (On-site, Phone, ...) and what
	 * came of it. The chosen value's NAME is stored on
	 * `m_sm_client_visit.visitType` / `.outcome` (free varchar), like call
	 * dispositions, so renames only affect future visits.
	 */
	ClientVisitType = 'client_visit_type',
	ClientVisitOutcome = 'client_visit_outcome',
	/**
	 * PD-17101 — why an opportunity (and, from PD-17102, a quote) was lost.
	 * Stored by id (`fk_catalog_value__id_lostReason`) so the Win/Loss report
	 * groups by reason; a rename applies to past losses too.
	 */
	OpportunityLostReason = 'opportunity_lost_reason',
	/**
	 * PD-17794 — how serious a pursuit is (Macomb: Budget, Bid, Bid to Buy).
	 * Stored by id on the opportunity; an empty list hides the field.
	 */
	OpportunityType = 'opportunity_type',
	/**
	 * PD-17109 — where a competitor on a job stands (Quoting, Supplying, Won,
	 * Lost). Stored by id on `job_competitor`; each value's `outcome` maps it
	 * to a fixed COMPETITOR_ENGAGEMENT_STATUS_OUTCOMES value.
	 */
	CompetitorEngagementStatus = 'competitor_engagement_status',
	/**
	 * PD-17606 — Workd's categories for an email reply to a campaign (Needs a
	 * Look, Interested, Wants a Meeting, ...). Each value's `outcome` maps it
	 * to a fixed REPLY_CATEGORY_MEANINGS value, which the campaign engine reads.
	 */
	ReplyCategory = 'reply_category',
	/**
	 * PD-17604 — where a person or company came from (Trade Show, Referral,
	 * Website, ...). Stored by id on `contact` and `client`
	 * (`fk_catalog_value__id_leadSource`); each value's `outcome` maps it to a
	 * fixed LEAD_SOURCE_MEANINGS value.
	 */
	LeadSource = 'lead_source',
	/**
	 * PD-17448 — a customer's role on a job (Contractor, General contractor,
	 * Owner, Bidding contractor, Other) and where it stands there (Bidding,
	 * Awarded, Lost, Not buying). Stored by id on `job_customer`; each status
	 * value's `outcome` maps it to a fixed JOB_CUSTOMER_STATUS_OUTCOMES value.
	 */
	JobCustomerRole = 'job_customer_role',
	JobCustomerStatus = 'job_customer_status',
	/**
	 * PD-17326 — the file types each kind of record offers on upload (RFQ,
	 * Specifications, Drawings, ...), one tenant-maintained list per record
	 * type. The chosen value's NAME is stored on `_file._subtype` (free
	 * varchar), like visit types, so renames only affect future uploads and
	 * older values (the codes the fixed customer list wrote) keep rendering.
	 * FILE_RECORD_TYPES (constants/file-types.ts) maps record types to these.
	 */
	ClientFileType = 'client_file_type',
	OpportunityFileType = 'opportunity_file_type',
	JobFileType = 'job_file_type',
	IssueFileType = 'issue_file_type',
	ItemFileType = 'item_file_type',
	LicenseFileType = 'license_file_type',
	SourcingRequestFileType = 'sourcing_request_file_type',
	PurchaseOrderReceiptFileType = 'purchase_order_receipt_file_type',
	/** PD-17589 — a person's files (a scanned business card, an agreement, a headshot). */
	ContactFileType = 'contact_file_type',
}
